import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';

import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';

const MIN_SEARCH_LENGTH = 3;

const normalizeSearch = (value: string) => value.split(' ').filter(Boolean).join(' ');

interface DataTableSearchFieldProps {
  loading?: boolean;
  onSearchChange: (searchValue: string) => void;
}

export default function DataTableSearchField({
  loading = false,
  onSearchChange,
}: DataTableSearchFieldProps) {
  const [inputValue, setInputValue] = useState('');
  const [searchedValue, setSearchedValue] = useState('');
  const [isAwaitingResults, setIsAwaitingResults] = useState(false);
  const hasSeenLoadingRef = useRef(false);
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;

  const search = (value: string) => {
    const searchValue = normalizeSearch(value);
    const isSearchable =
      searchValue.length === 0 || searchValue.length >= MIN_SEARCH_LENGTH;
    const isNewSearch = searchValue !== searchedValue;

    if (isSearchable && isNewSearch) {
      setSearchedValue(searchValue);
      hasSeenLoadingRef.current = false;
      setIsAwaitingResults(true);
      onSearchChangeRef.current(searchValue);
    }
  };
  const searchRef = useRef(search);
  searchRef.current = search;

  useEffect(() => {
    const timeout = setTimeout(
      () => searchRef.current(inputValue),
      GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS,
    );

    return () => clearTimeout(timeout);
  }, [inputValue]);

  // Consumers start their fetch a render after onSearchChange, so `loading` can lag the
  // search; give up waiting if it never starts, so the spinner can't stick.
  useEffect(() => {
    if (!isAwaitingResults) {
      return;
    }

    if (loading) {
      hasSeenLoadingRef.current = true;

      return;
    }

    if (hasSeenLoadingRef.current) {
      setIsAwaitingResults(false);

      return;
    }

    const timeout = setTimeout(
      () => setIsAwaitingResults(false),
      GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS,
    );

    return () => clearTimeout(timeout);
  }, [isAwaitingResults, loading]);

  const typedValue = normalizeSearch(inputValue);
  const isTooShort = typedValue.length > 0 && typedValue.length < MIN_SEARCH_LENGTH;
  const isWaitingToSearch = !isTooShort && typedValue !== searchedValue;
  const isPending = isWaitingToSearch || isAwaitingResults;

  const handleClear = () => {
    setInputValue('');
    search('');
  };

  return (
    <Tooltip
      open={isTooShort}
      placement="bottom-start"
      title={t('data_table.search_min_length', { count: MIN_SEARCH_LENGTH })}
    >
      <TextField
        onChange={(event) => setInputValue(event.target.value)}
        placeholder={t('form_fields.search')}
        size="small"
        value={inputValue}
        slotProps={{
          htmlInput: { 'aria-label': t('form_fields.search') },
          input: {
            startAdornment: (
              <InputAdornment position="start" sx={{ color: 'text.primary', mr: 0 }}>
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {isPending && (
                  <CircularProgress aria-label={t('common.loading')} size={16} />
                )}
                {inputValue && (
                  <IconButton
                    aria-label={t('common.clear')}
                    edge="end"
                    onClick={handleClear}
                    size="small"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                )}
              </>
            ),
          },
        }}
        sx={{ mr: 2, pb: 0.5, width: '227px', '& input': { ml: 0.5 } }}
      />
    </Tooltip>
  );
}
